import React, { useCallback, useState } from "react";
import { Picker } from "@design-system/Picker/Picker.lib";
import { useEntities } from "@hooks/useEntities";
import { PickerEntityItem } from "@components/entity/PickerEntityItem";
import { FieldButtonProps } from "./SelectedTransactionsUpdateButtonTypes";
import { UpdateButtonContent } from "./SelectedTransactionsUpdateButtonCommon";
import { useSelectedTransactionsUpdateShortcut } from "./useSelectedTransactionsUpdateShortcut";
import styles from "./ActionsCard/ActionsCard.module.scss";

export const EntityUpdateButton: React.FC<FieldButtonProps> = ({
    propertyLabel,
    selectedTransactions,
    applyValue,
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const entities = useEntities({ excludeMockEntities: true });

    useSelectedTransactionsUpdateShortcut({
        shortcut: "e",
        enabled: selectedTransactions.length > 0 && entities.length > 1,
        propertyLabel,
        open: () => setIsOpen(true),
    });

    const handleEntityChange = useCallback(
        (entityId: number | null) => {
            if (entityId === null) {
                setIsOpen(false);
                return;
            }

            const selectedEntity = entities.find(
                (entity) => entity.id === entityId,
            );
            if (selectedEntity) {
                applyValue(selectedEntity);
            }
            setIsOpen(false);
        },
        [applyValue, entities],
    );

    if (entities.length <= 1) {
        return null;
    }

    return (
        <Picker.Select<number>
            open={isOpen}
            onOpenChange={setIsOpen}
            onValueChange={handleEntityChange}
        >
            <Picker.ButtonTrigger
                render={
                    <Picker.Button
                        variant="primary"
                        size="md"
                        className={styles.updateButton}
                    />
                }
                value={
                    <UpdateButtonContent
                        propertyLabel={propertyLabel}
                        shortcut="e"
                    />
                }
                showChevron={false}
            />
            <Picker.Popup side="top" align="end">
                <Picker.List>
                    {entities.map((entity) => (
                        <PickerEntityItem
                            key={entity.id}
                            value={entity.id}
                            entity={entity}
                        />
                    ))}
                </Picker.List>
            </Picker.Popup>
        </Picker.Select>
    );
};
